import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { financeiroApi } from '../services/api';
import { Zap, Save, RotateCcw, CheckCircle2 } from 'lucide-react';

export function EntradaRapidaPage() {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [salvo, setSalvo] = useState(false);
  const nomeRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const entradaRapida = useMutation({
    mutationFn: (data: any) => financeiroApi.entradaRapida(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro'] });
      queryClient.invalidateQueries({ queryKey: ['resumo-dia'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setSalvo(true);
      setNome('');
      setDescricao('');
      setValor('');
      setTimeout(() => {
        setSalvo(false);
        nomeRef.current?.focus();
      }, 900);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome || !descricao || !valor) return;
    entradaRapida.mutate({
      nome,
      descricao,
      valor: parseFloat(valor),
    });
  };

  const limpar = () => {
    setNome('');
    setDescricao('');
    setValor('');
    nomeRef.current?.focus();
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl shadow-lg shadow-amber-500/30 mb-5">
          <Zap className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-display text-2xl font-bold text-ink-950 tracking-tight">Entrada Rápida</h1>
        <p className="text-sm text-ink-500 mt-2">Lance vendas do balcão em poucos segundos, sem abrir pedido.</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        {salvo && (
          <div className="flex items-center justify-center gap-2 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-[13px] font-semibold text-emerald-700 animate-fade-in">
            <CheckCircle2 className="w-4 h-4" />
            Lançamento salvo! Pode lançar o próximo.
          </div>
        )}

        <div>
          <label className="input-label" htmlFor="nome">Nome do Cliente</label>
          <input
            id="nome"
            ref={nomeRef}
            type="text"
            value={nome}
            onChange={e => setNome(e.target.value)}
            placeholder="Ex: João, Escolar — Colégio X"
            className="input-field"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="input-label" htmlFor="descricao">Descrição</label>
          <input
            id="descricao"
            type="text"
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            placeholder="Ex: 2 camisetas, 1 calça"
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="input-label" htmlFor="valor">Valor Total</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-400">R$</span>
            <input
              id="valor"
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={e => setValor(e.target.value)}
              placeholder="0,00"
              className="input-field !pl-10 !py-3 font-display text-lg font-bold"
              required
            />
          </div>
        </div>

        <div className="pt-1 flex gap-3">
          <button type="button" onClick={limpar} className="btn-secondary flex-1">
            <RotateCcw className="w-4 h-4" />
            Limpar
          </button>
          <button type="submit" disabled={entradaRapida.isPending} className="btn-primary flex-[2]">
            <Save className="w-4 h-4" />
            {entradaRapida.isPending ? 'Salvando...' : 'Salvar e Próximo'}
          </button>
        </div>
      </form>
    </div>
  );
}
